import type { ICache } from "@polygonlabs/servercore";
import Redis, { Cluster, type ClusterOptions } from "ioredis";
import type { RedisClientOptions } from "./interfaces";

export class RedisClient implements ICache {
    private readonly client: Redis | Cluster;
    private isConnected: boolean = false;

    constructor(options: RedisClientOptions = {}) {
        if (options.cluster) {
            const clusterNodes = options.cluster.nodes.map((node) => ({
                host: node.host,
                port: node.port,
            }));

            const clusterOptions: ClusterOptions = {
                ...options.cluster.options,
                redisOptions: {
                    password: options.password,
                    keyPrefix: options.keyPrefix,
                    maxRetriesPerRequest: options.maxRetriesPerRequest || 3,
                    lazyConnect: options.lazyConnect !== false,
                },
            };

            this.client = new Cluster(clusterNodes, clusterOptions);
        } else {
            this.client = new Redis({
                host: options.host || "localhost",
                port: options.port || 6379,
                password: options.password,
                db: options.db || 0,
                keyPrefix: options.keyPrefix,
                maxRetriesPerRequest: options.maxRetriesPerRequest || 3,
                lazyConnect: options.lazyConnect !== false,
            });
        }

        this.client.on("connect", () => {
            this.isConnected = true;
        });

        this.client.on("close", () => {
            this.isConnected = false;
        });
    }

    async connect(): Promise<void> {
        if (!this.isConnected) {
            await this.client.connect();
        }
    }

    async disconnect(): Promise<void> {
        if (this.isConnected) {
            this.client.disconnect();
        }
    }

    async get<T = any>(key: string): Promise<T | null> {
        const result = await this.client.get(key);
        if (result === null) return null;

        try {
            return JSON.parse(result);
        } catch {
            return result as T;
        }
    }

    async set<T = any>(
        key: string,
        value: T,
        ttlSeconds?: number
    ): Promise<void> {
        const serializedValue =
            typeof value === "string" ? value : JSON.stringify(value);

        if (ttlSeconds) {
            await this.client.setex(key, ttlSeconds, serializedValue);
        } else {
            await this.client.set(key, serializedValue);
        }
    }

    async delete(key: string): Promise<boolean> {
        const result = await this.client.del(key);
        return result > 0;
    }

    async exists(key: string): Promise<boolean> {
        const result = await this.client.exists(key);
        return result > 0;
    }

    async clear(pattern: string = "*"): Promise<void> {
        const keys = await this.client.keys(pattern);

        if (keys.length > 0) {
            await this.client.del(...keys);
        }
    }

    async expire(key: string, ttlSeconds: number): Promise<boolean> {
        const result = await this.client.expire(key, ttlSeconds);
        return result === 1;
    }

    async ttl(key: string): Promise<number> {
        return await this.client.ttl(key);
    }

    async keys(pattern?: string): Promise<string[]> {
        return await this.client.keys(pattern || "*");
    }

    // Hash operations
    async hset<T = any>(key: string, field: string, value: T): Promise<void> {
        const serializedValue =
            typeof value === "string" ? value : JSON.stringify(value);
        await this.client.hset(key, field, serializedValue);
    }

    async hget<T = any>(key: string, field: string): Promise<T | null> {
        const result = await this.client.hget(key, field);
        if (result === null) return null;

        try {
            return JSON.parse(result);
        } catch {
            return result as T;
        }
    }

    async hgetall<T = any>(key: string): Promise<Record<string, T>> {
        const result = await this.client.hgetall(key);
        const parsed: Record<string, T> = {};

        for (const [field, value] of Object.entries(result)) {
            try {
                parsed[field] = JSON.parse(value);
            } catch {
                parsed[field] = value as T;
            }
        }

        return parsed;
    }

    async hdel(key: string, ...fields: string[]): Promise<number> {
        return await this.client.hdel(key, ...fields);
    }

    async hexists(key: string, field: string): Promise<boolean> {
        const result = await this.client.hexists(key, field);
        return result === 1;
    }

    async hkeys(key: string): Promise<string[]> {
        return await this.client.hkeys(key);
    }

    async hlen(key: string): Promise<number> {
        return await this.client.hlen(key);
    }

    async hmset<T = any>(key: string, hash: Record<string, T>): Promise<void> {
        const serializedHash: Record<string, string> = {};
        for (const [field, value] of Object.entries(hash)) {
            serializedHash[field] =
                typeof value === "string" ? value : JSON.stringify(value);
        }
        await this.client.hmset(key, serializedHash);
    }

    async hmget<T = any>(
        key: string,
        ...fields: string[]
    ): Promise<(T | null)[]> {
        const results = await this.client.hmget(key, ...fields);
        return results.map((result) => {
            if (result === null) return null;
            try {
                return JSON.parse(result);
            } catch {
                return result as T;
            }
        });
    }
}
